import { inject, Injector, Pipe, PipeTransform, signal, untracked, type Signal } from '@angular/core';
import { FormArray, FormGroup, type AbstractControl } from '@angular/forms';
import { derivedAsync } from 'ngxtension/derived-async';
import { map, startWith } from 'rxjs';

@Pipe({
    name: 'formControlErrors',
    standalone: true,
})
export class FormControlErrorsPipe implements PipeTransform {
    protected readonly injector = inject(Injector);

    errorMap: Record<string, string | ((error: Record<string, unknown>) => string)> = {
        required: 'Dieses Feld ist erforderlich.',
        email: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
        maxlength: (error) => `Maximal ${error['requiredLength']} Zeichen.`,
        minLengthArray: (error) =>
            `Mindestens ${error['minLength']} Element${(error['minLength'] as number) > 1 ? 'e' : ''} erforderlich.`,
        pattern: (error) => (typeof error === 'string' ? error : 'Ungültige Eingabe'),
    };

    abstractControl = signal<AbstractControl | null>(null);

    errors = derivedAsync(() => {
        const abstractControl = this.abstractControl();
        if (!abstractControl) {
            return;
        }

        return abstractControl.statusChanges.pipe(
            startWith(abstractControl.status),
            map((status) => {
                const isFormArrayOrGroup = abstractControl instanceof FormArray || abstractControl instanceof FormGroup;
                if (
                    status !== 'INVALID' ||
                    !abstractControl.errors ||
                    (!isFormArrayOrGroup && abstractControl.touched && !abstractControl.errors['pattern'])
                ) {
                    return [];
                }
                return Object.entries(abstractControl.errors)
                    .flatMap(([key, value]) => {
                        if (typeof this.errorMap[key] === 'function') {
                            return this.errorMap[key](value);
                        }
                        return this.errorMap[key] ?? value;
                    })
                    .filter((error) => Boolean(error));
            }),
        );
    });

    transform(
        abstractControl: AbstractControl,
        errorMap?: Record<string, string | ((error: Record<string, unknown>) => string)>,
    ): Signal<string[] | undefined> {
        if (errorMap) {
            Object.assign(this.errorMap, errorMap);
        }
        untracked(() => {
            this.abstractControl.set(abstractControl);
        });
        return this.errors;
    }
}
